import { z } from 'zod';
import { isoDate, memberRole, phone, trimmed, uuid } from './common.js';

export const communitySettings = z.object({
  /** Seconds a walk-in waits for a resident before becoming "no response". */
  approvalTimeoutSec: z.number().int().min(30).max(900).default(120),
  /** What guards may do when nobody answers. */
  noResponsePolicy: z.enum(['guard_discretion', 'deny']).default('guard_discretion'),
  /** Visitor photos are deleted after this many days. */
  photoRetentionDays: z.number().int().min(1).max(365).default(60),
  /** Day of the month maintenance bills fall due (1-28 so it exists in every month). */
  invoiceDueDay: z.number().int().min(1).max(28).default(10),
  /** Company name the accountant's Tally uses; written into the Tally export. */
  tallyCompanyName: z.string().trim().min(1).max(100).default('Expansio'),
  /** How often maintenance is billed. Quarterly bills start in Apr/Jul/Oct/Jan; half-yearly in Apr/Oct. */
  billingFrequency: z.enum(['monthly', 'quarterly', 'half_yearly']).default('monthly'),
});
export type CommunitySettings = z.infer<typeof communitySettings>;

export const communityDto = z.object({
  id: uuid,
  name: z.string(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  timezone: z.string(),
  settings: communitySettings,
});
export const updateCommunity = z.object({
  name: trimmed(2, 120).optional(),
  address: trimmed(1, 300).nullable().optional(),
  settings: communitySettings.partial().optional(),
});

export const blockDto = z.object({ id: uuid, name: z.string() });
export const unitDto = z.object({
  id: uuid,
  blockId: uuid,
  blockName: z.string(),
  number: z.string(),
  floor: z.number().nullable(),
  kind: z.enum(['apartment', 'villa', 'shop', 'other']),
  label: z.string(),
  areaSqft: z.number().nullable(),
});
export type UnitDto = z.infer<typeof unitDto>;

/** Bulk unit setup: [{ block: "A", units: ["101","102"] }] */
export const bulkUnits = z.object({
  blocks: z
    .array(
      z.object({
        block: trimmed(1, 40),
        units: z.array(trimmed(1, 20)).min(1).max(1000),
        /** Area in sq ft given to every new flat in this block (optional; editable per flat later). */
        areaSqft: z.number().positive().max(1_000_000).multipleOf(0.01).optional(),
      }),
    )
    .min(1)
    .max(100),
});

export const memberDto = z.object({
  membershipId: uuid,
  userId: uuid,
  name: z.string().nullable(),
  phone: z.string(),
  role: memberRole,
  title: z.string().nullable(),
  unitId: uuid.nullable(),
  unitLabel: z.string().nullable(),
  canApproveVisitors: z.boolean(),
  status: z.enum(['active', 'suspended', 'ended']),
});
export type MemberDto = z.infer<typeof memberDto>;

export const addMember = z
  .object({
    phone,
    name: trimmed(1, 80),
    role: memberRole,
    unitId: uuid.optional(),
    title: trimmed(1, 60).optional(),
    canApproveVisitors: z.boolean().default(true),
  })
  .refine((v) => (['owner', 'tenant', 'family'].includes(v.role) ? !!v.unitId : !v.unitId), {
    message: 'Residents need a unit; guards, staff and admins must not have one',
    path: ['unitId'],
  });

export const updateMember = z.object({
  status: z.enum(['active', 'suspended', 'ended']).optional(),
  canApproveVisitors: z.boolean().optional(),
  title: trimmed(1, 60).nullable().optional(),
});

/** Bulk import residents, e.g. from the society's existing spreadsheet. */
export const bulkMembers = z.object({
  rows: z
    .array(
      z.object({
        block: trimmed(1, 40),
        unit: trimmed(1, 20),
        name: trimmed(1, 80),
        phone,
        role: z.enum(['owner', 'tenant']).default('owner'),
      }),
    )
    .min(1)
    .max(2000),
});
export const bulkMembersResult = z.object({
  created: z.number(),
  skipped: z.array(z.object({ row: z.number(), reason: z.string() })),
});

export const loginCodeResponse = z.object({
  code: z.string(),
  expiresAt: isoDate,
  phone: z.string(),
});
